import styled from 'styled-components';

import { Button } from '@/components/ui/button';

import { TrelloView } from 'components/TrelloView/TrelloView';

import { OrderTrelloItem } from './components/OrderTrelloItem';
import {
    ORDER_BUCKET_ORDER,
    ORDER_COLUMN_LABELS,
    groupOrdersIntoBuckets,
    type OrderRow,
} from './constants/orderBuckets';

type OrdersTrelloViewProps = {
    orders: OrderRow[];
    selectedOrderIds: string[];
    setSelectedOrderIds: React.Dispatch<React.SetStateAction<string[]>>;
    toggleOrderSelection: (orderId: string) => void;
    /** Le kanban n'a pas de défilement unique où accrocher un chargement continu : il le demande. */
    hasMore: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
};

export const OrdersTrelloView = ({
    orders,
    selectedOrderIds,
    setSelectedOrderIds,
    toggleOrderSelection,
    hasMore,
    isLoadingMore,
    onLoadMore,
}: OrdersTrelloViewProps) => {
    const buckets = groupOrdersIntoBuckets(orders);

    const initialData = {
        columns: ORDER_BUCKET_ORDER.map((bucket) => ({
            title: ORDER_COLUMN_LABELS[bucket],
            items: buckets[bucket].map((order) => ({
                _id: order._id,
                content: (
                    <OrderTrelloItem
                        order={order}
                        isSelected={selectedOrderIds.includes(order._id)}
                        toggleSelection={() => toggleOrderSelection(order._id)}
                    />
                ),
            })),
        })),
    };

    return (
        <TrelloViewContainer>
            {hasMore ? (
                // Le compte des colonnes ne porte que sur ce qui est chargé : le dire, plutôt que de
                // laisser croire qu'une colonne courte est une colonne vide.
                <LoadMoreBanner>
                    <span>
                        {orders.length} commandes chargées — il en reste au-delà, les colonnes ne les comptent pas
                        encore.
                    </span>
                    <Button type="button" variant="outline" size="sm" onClick={onLoadMore} disabled={isLoadingMore}>
                        {isLoadingMore ? 'Chargement…' : 'Charger la suite'}
                    </Button>
                </LoadMoreBanner>
            ) : null}
            <TrelloScrollArea>
                <TrelloView
                    initialData={initialData}
                    selectedItemIds={selectedOrderIds}
                    setSelectedItemIds={setSelectedOrderIds}
                    columnWidth="250px"
                    padding="0"
                />
            </TrelloScrollArea>
        </TrelloViewContainer>
    );
};

/**
 * Colonne flex : la bannière prend sa hauteur propre, le kanban le reste. Sans ça, `TrelloView` fait
 * `h-full` de CE conteneur et s'ajoute à la bannière — d'où une barre de défilement verticale qui
 * apparaissait en même temps qu'elle.
 */
const TrelloViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    min-height: 0;
`;

/** Le défilement horizontal vit ici. Jamais de vertical : chaque colonne a déjà le sien. */
const TrelloScrollArea = styled.div`
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow-x: auto;
    overflow-y: hidden;
`;

const LoadMoreBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 8px 12px;
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
`;
